import { BANNED_URLS, PAGE_TYPES } from '../assistant-config';

export const getPortalRootNode = () => {
  return document?.querySelector('#root');
};

export const getStickyATCElement = () => {
  // Query elements matching the markup added by the Sticky component
  const buyboxMatch = document.querySelector(
    'div[data-component^=buybox\\:Buybox\\:v] [style^=min-height] > [class*=sticky]'
  );
  const applianceBuyboxMatch = document.querySelector(
    'div[data-component^=buybox\\:MajorApplianceBuyBox\\:v] [style^=min-height] > [class*=sticky]'
  );
  const buyboxActionsMatch = document.querySelector(
    'div[data-component^=buybox\\:BuyboxActions\\:v] [style^=min-height] > [class*=sticky]'
  );
  const buyboxSimpleMatch = document.querySelector(
    'div[data-component^=Buybox] [style^=min-height] > [class*=sticky]'
  );

  // Use the more specific section if available
  if (buyboxActionsMatch) return buyboxActionsMatch;

  if (applianceBuyboxMatch) return applianceBuyboxMatch;

  if (buyboxSimpleMatch) return buyboxSimpleMatch;

  // Use the generic (older) version as fallback
  if (buyboxMatch) return buyboxMatch;

  return null;
};

export const getPath = () => {
  return typeof window !== 'undefined' ? new URL(window.document.documentURI).pathname : '';
};

// ⚠ TWO page-type vocabularies exist and they are not interchangeable.
//   - This file's classifier answers "is the assistant ALLOWED on this page",
//     over the loader's own PAGE_TYPES enum (assistant-config). Frozen by
//     MAG4-857 so getIsLegacyCompatiblePage() keeps behaving exactly as it has.
//     Private on purpose: nothing else should use it.
//   - `window-utils` owns the two RUNTIME getters — getCanonicalPageType() (the
//     site's open-ended analytics value) and getLegacyPageType() (the closed
//     vocabulary the planner service validates). Those are the ones features use.
// Master grew a copy of the runtime resolver here; it was dropped rather than
// merged, because a single resolver in window-utils is a decision this branch
// already made and duplicating it is how the two drift apart.
const legacyCompatibilityPageType = () => {
  const pathArray = getPath()?.split('/') || [];

  if (pathArray[1] === 'c') return PAGE_TYPES.CONTENT;
  if (pathArray[1] === 'p') {
    if (pathArray[2] === 'sets') return PAGE_TYPES.BIP;
    return PAGE_TYPES.PIP;
  }
  if (pathArray[1] === 's') return PAGE_TYPES.SEARCH;
  if (pathArray[1] === 'b') return PAGE_TYPES.BROWSE;
  if (pathArray[1] === '') return PAGE_TYPES.HOME;

  return PAGE_TYPES.UNKNOWN;
};

// ⚠ `isSessionExpired`, `getSessionId`, `createActionId` and `getDefaultSession` are NOT
// here. They are owned by `platform/session/session.js`, the single owner of the three
// `maa-*` records (Seq B.3). Seq 8.2 deleted the re-export shims that used to sit in this
// file and repointed every importer.
//
// The master merge reintroduced all four, because master never had that split — and its
// `getDefaultSession` was the pre-B.3 FAT record (`shouldAutoOpen`,
// `hasSessionInteractedWithFab`, `pagesExpandedFABShownOn`) whose fields now live in the
// separate `maa-drawer-prefs` and `maa-live-agent` records. Two disagreeing
// `getDefaultSession`s in one tree is exactly the lost-update hazard the split removed, so
// they were deleted again rather than reconciled. No `src/` module imported them from here.

export const getIsLegacyCompatiblePage = () => {
  if (BANNED_URLS.includes(getPath())) {
    return false;
  }

  return [
    PAGE_TYPES.HOME,
    PAGE_TYPES.PIP,
    PAGE_TYPES.BROWSE,
    PAGE_TYPES.SEARCH,
    PAGE_TYPES.CONTENT,
  ].includes(legacyCompatibilityPageType());
};

// Entries are exact paths (e.g. '/c/customer-service') or prefixes ending in
// '/*' (e.g. '/p/*') to exclude the sub-paths of a whole path family. Both forms
// match the end of the current path, so they still apply on URLs carrying a
// wrapper prefix (e.g. '/render-nucleus/c/customer-service').
const parseExcludedEntries = (configValue) => {
  if (!configValue) {
    return [];
  }

  return configValue
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
};

const matchesExcludedEntry = (path, entry) => {
  if (!entry.endsWith('/*')) {
    return path.endsWith(entry);
  }

  return path.includes(entry.slice(0, -1));
};

// The assistant already renders on the parent page, so a second
// instance inside the frame is never wanted — and the frame's own path is not a page
// the exclusion list can reasonably enumerate.
export const getIsInIframe = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.self !== window.top;
  } catch {
    // Reading window.top can throw behind a cross-origin parent, which only ever
    // happens when we are framed.
    return true;
  }
};

export const getIsNewCompatiblePage = (excludedPagesConfig) => {
  const path = getPath();
  const excludedEntries = parseExcludedEntries(excludedPagesConfig);
  const isExcluded = excludedEntries.some((entry) => matchesExcludedEntry(path, entry));

  if (isExcluded) {
    return false;
  }

  return true;
};

export const waitForIdle = (callback, timeout = 3000) => {
  const run = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout });
    } else {
      // fallback for Safari/older browsers
      setTimeout(callback, 0);
    }
  };

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
};

export const HOST_CONSTANTS = {
  // eslint-disable-next-line
  DEFAULT_HOST: typeof import.meta.env.MAA__DEFAULT_HOST__ !== 'undefined' ?  import.meta.env.MAA__DEFAULT_HOST__ : '',
  GRAPHQL_HOST: {
    DEV: 'https://apionline-dev.homedepotdev.com',
    STAGE: 'https://apionline-stage.homedepotdev.com',
    PROD: 'https://apionline.homedepot.com'
  },
  ENV: {
    DEV: 'hd-qa71',
    STAGE: 'hd-qa74',
    LOCAL: 'vite'
  }
};

export const getGraphqlHost = () => {
  const currentHost = typeof window !== 'undefined' ? window?.location?.host?.split('.')[0] : null;
  const { ENV, GRAPHQL_HOST, DEFAULT_HOST } = HOST_CONSTANTS;
  const LOCAL_ENV = import.meta.env.mode;

  switch (currentHost) {
  case ENV.DEV: {
    return GRAPHQL_HOST.DEV;
  }
  case ENV.STAGE: {
    return GRAPHQL_HOST.STAGE;
  }
  case ENV.LOCAL: {
    return DEFAULT_HOST;
  }
  default: {
    return GRAPHQL_HOST.PROD;
  }
  }
};